import LastKnownReadingDTO from "features/contracts/dtos/LastKnownReadingDTO";
import { ContractType } from "features/contracts/enums/contractType";
import {
  ValidationActionType,
  ValueActionType,
} from "features/shared/forms/hooks/useForm";
import { Nullable } from "features/shared/types/nullable";
import FunctionValidationRule from "features/validation/rules/FunctionValidationRule";
import ValidationRule from "features/validation/rules/ValidationRule";
import { TFunction } from "i18next";

export default class InvoiceSimulationSectionHandler {
  private readonly _setValidationRule: (
    key: string,
    validationRule: Nullable<ValidationRule>,
    action: ValidationActionType
  ) => void;

  private readonly _setFormValue: (
    key: string,
    value: any,
    action: ValueActionType
  ) => void;

  private readonly _transalte: TFunction;

  constructor(
    setValidationRule: (
      key: string,
      validationRule: Nullable<ValidationRule>,
      action: ValidationActionType
    ) => void,
    setFormValue: (key: string, value: any, action: ValueActionType) => void,
    translate: TFunction
  ) {
    this._setValidationRule = setValidationRule;
    this._setFormValue = setFormValue;
    this._transalte = translate
  }

  // FUNCTIONS
  clearFormValuesByContractType(contractType?: ContractType) {
    if (!contractType) return;

    if (contractType === ContractType.ElectricityLowVoltage)
      this.clearFormValues(["STAV"]);

    if (contractType === ContractType.GasSmallConsumption)
      this.clearFormValues(["STAV_NT", "STAV_VT"]);
  }

  clearFormValues(formKeys: string[]): void {
    for (const key of formKeys) {
      this._setFormValue(key, null, "remove");
    }
  }

  getLastKnownReadingPlaceholder(
    type: string,
    lastKnownReading: LastKnownReadingDTO,
    contractType?: ContractType
  ): string {
    const defaultPlaceholder = this._transalte("invoiceSimulationSectionHandler.defaultPlaceholder");

    const resultTemplate = (value?: string): string => {
      if (!value) return defaultPlaceholder;
      return this._transalte("invoiceSimulationSectionHandler.lastKnowValuePlaceholder", { value });
    };

    if (Object.keys(lastKnownReading).length === 0) return defaultPlaceholder;

    if (contractType === ContractType.GasSmallConsumption && type === "STAV")
      return resultTemplate(lastKnownReading?.value?.toString());

    if (
      contractType === ContractType.ElectricityLowVoltage &&
      type === "STAV_NT"
    )
      return resultTemplate(lastKnownReading?.valueNT?.toString());

    if (
      contractType === ContractType.ElectricityLowVoltage &&
      type === "STAV_VT"
    )
      return resultTemplate(lastKnownReading?.value?.toString());

    return defaultPlaceholder;
  }

  // VALIDATIONS
  setValidationByContractType(
    contractType: ContractType,
    lastKnownReading: LastKnownReadingDTO
  ) {
    if (contractType === ContractType.ElectricityLowVoltage) {
        this.setValidationForElectricityLowVoltageNT(lastKnownReading);
        this.setValidationForElectricityLowVoltageVT(lastKnownReading);
        this._setValidationRule("STAV", null, "remove");
    }
      
    if (contractType === ContractType.GasSmallConsumption) {
        this.setValidationForGasSmallConsumption(lastKnownReading);
        this._setValidationRule("STAV_NT", null, "remove");
        this._setValidationRule("STAV_VT", null, "remove");
    }      
  }

  private setValidationForElectricityLowVoltageNT(
    lastKnownReading: LastKnownReadingDTO
  ) {
    const lastKnownReadingValueNT = lastKnownReading.valueNT ?? 0;

    const validationFunction = (value: any) => {
      if (!value) return true;
      return (value as number) >= lastKnownReadingValueNT;
    };

    const rule: FunctionValidationRule = {
      required: false,
      type: "Function",
      message: this._transalte("invoiceSimulationSectionHandler.invalidNtValue", { lastKnownReadingValueNT }),
      validate: validationFunction,
    };

    this._setValidationRule("STAV_NT", rule, "add");
  }

  private setValidationForElectricityLowVoltageVT(
    lastKnownReading: LastKnownReadingDTO
  ) {
    const lastKnownReadingValueVT = lastKnownReading.value ?? 0;

    const validationFunction = (value: any) => {
      if (!value) return true;
      return (value as number) >= lastKnownReadingValueVT;
    };

    const rule: FunctionValidationRule = {
      required: false,
      type: "Function",
      message: this._transalte("invoiceSimulationSectionHandler.invalidVtValue", { lastKnownReadingValueVT }),
      validate: validationFunction,
    };

    this._setValidationRule("STAV_VT", rule, "add");
  }

  private setValidationForGasSmallConsumption(
    lastKnownReading: LastKnownReadingDTO
  ) {
    const lastKnownReadingValue = lastKnownReading.value ?? 0;

    const validationFunction = (value: any) => {
      if (!value) return true;
      return (value as number) >= lastKnownReadingValue;
    };

    const rule: FunctionValidationRule = {
      required: false,
      type: "Function",
      message: this._transalte("invoiceSimulationSectionHandler.invalidValue", { lastKnownReadingValue }),
      validate: validationFunction,
    };

    this._setValidationRule("STAV", rule, "add");
  }
}